import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import { motion } from 'motion/react'
import { Logo } from '@/components/generals/logo'
import { ButtonLink } from '@/components/generals/button'
import { footerColumns, primaryCta, siteConfig } from '@/constants/site'

const SiteFooter: FC = (): ReactNode => {
    return (
        <footer className='relative overflow-hidden bg-forest text-forest-foreground'>
            <div className='mx-auto max-w-7xl px-4 pb-10 pt-20 sm:px-6 lg:px-8'>
                <div className='grid gap-12 lg:grid-cols-[1.3fr_2fr]'>
                    <div className='max-w-sm'>
                        <Link
                            href='/'
                            aria-label={`${siteConfig.name} home`}
                            className='inline-flex rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-leaf'
                        >
                            <Logo tone='light' />
                        </Link>

                        <p className='mt-5 text-[15px] leading-relaxed text-forest-foreground/70'>
                            {siteConfig.tagline} Forecast the peak, store the surplus and bid at the right moment, from one calm workspace.
                        </p>

                        <ButtonLink
                            href={primaryCta.href}
                            variant='onDark'
                            className='mt-7'
                        >
                            {primaryCta.label}
                            <ArrowUpRight className='size-4' aria-hidden='true' />
                        </ButtonLink>
                    </div>

                    <div className='grid grid-cols-2 gap-10 sm:grid-cols-3'>
                        {footerColumns.map((column) => (
                            <div key={column.title}>
                                <h2 className='text-sm font-semibold text-white'>
                                    {column.title}
                                </h2>

                                <ul className='mt-4 space-y-3'>
                                    {column.links.map((link) => (
                                        <li key={`${column.title}-${link.label}`}>
                                            <Link
                                                href={link.href}
                                                className='text-sm text-forest-foreground/65 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-leaf rounded'
                                            >
                                                {link.label}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>

                <div className='mt-16 flex flex-col gap-3 border-t border-white/10 pt-6 text-sm text-forest-foreground/55 sm:flex-row sm:items-center sm:justify-between'>
                    <p>
                        &copy; {siteConfig.year} {siteConfig.name}. All rights reserved.
                    </p>

                    <p>
                        Built for a steadier, cleaner grid.
                    </p>
                </div>
            </div>

            <motion.div
                aria-hidden='true'
                initial='hidden'
                whileInView='visible'
                viewport={{ once: true, margin: '0px 0px -5% 0px' }}
                variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.06 } } }}
                className='pointer-events-none flex select-none justify-center overflow-hidden px-2 font-heading text-[23vw] font-semibold leading-[0.78] tracking-[-0.06em]'
            >
                {siteConfig.name.split('').map((letter, index) => (
                    <motion.span
                        key={`${letter}-${index}`}
                        variants={{
                            hidden: { y: '100%', opacity: 0 },
                            visible: { y: '0%', opacity: 1, transition: { duration: 1.1, ease: [0.22, 1, 0.36, 1] } }
                        }}
                        className='inline-block wordmark-footer'
                    >
                        {letter}
                    </motion.span>
                ))}
            </motion.div>
        </footer>
    )
}

export default SiteFooter
